import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ROLE_PERMISSIONS, type Role } from '@prody/shared';
import {
  Button, DataTable, EmptyState, IconPlus, ListPane, PageHeader, Stamp,
  Toolbar, Tooltip, Workbench, type Column,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useTeam } from '~/platform/data/queries';

/* ==================================================================
   Team.

   The role column is a link to what the role can actually do, because
   "why can't the driver see prices" is asked in every deployment and
   the answer should be one hover away rather than a support ticket.
   ================================================================== */

type Member = NonNullable<ReturnType<typeof useTeam>['data']>[number];

export default function TeamPage() {
  const { t } = useTranslation(['team', 'common']);
  const { business, format, role: myRole } = useBusiness();
  const { data: members = [], isLoading } = useTeam(business.id);

  const canInvite = myRole === 'owner' || myRole === 'manager';

  const columns = useMemo<Column<Member>[]>(
    () => [
      {
        key: 'name',
        header: t('common:labels.customer'),
        width: 'minmax(160px, 1.6fr)',
        sortable: true,
        sortValue: (m) => m.name,
        cell: (m) => <span className="truncate">{m.name}</span>,
      },
      {
        key: 'email',
        header: t('common:labels.none'),
        width: 'minmax(180px, 1.4fr)',
        hideBelow: 'sm',
        cell: (m) => <span className="num truncate dim">{m.email}</span>,
      },
      {
        key: 'role',
        header: t('role'),
        width: '150px',
        sortable: true,
        sortValue: (m) => m.role,
        cell: (m) => (
          <Tooltip content={permissionSummary(m.role)}>
            <span>
              <Stamp tone={m.role === 'owner' ? 'accent' : 'neutral'}>
                {t(`common:role.${m.role}`)}
              </Stamp>
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'extra',
        header: t('common:actions.more'),
        width: '160px',
        hideBelow: 'lg',
        cell: (m) =>
          m.extraPermissions.length ? (
            <span className="num t-small dim truncate">{m.extraPermissions.join(', ')}</span>
          ) : (
            <span className="faint">—</span>
          ),
      },
      {
        key: 'status',
        header: t('common:labels.status'),
        width: '116px',
        cell: (m) => (
          <Stamp
            tone={m.status === 'active' ? 'ok' : m.status === 'invited' ? 'warn' : 'neutral'}
            weight={m.status === 'disabled' ? 'solid' : 'outline'}
          >
            {t(`status.${m.status}`)}
          </Stamp>
        ),
      },
      {
        key: 'seen',
        header: t('lastSeen'),
        align: 'num',
        width: '120px',
        hideBelow: 'md',
        cell: (m) => <span className="num faint">{format.date(m.updatedAt.slice(0, 10))}</span>,
      },
    ],
    [t, format],
  );

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={isLoading ? undefined : t('common:labels.results', { count: members.length })}
        actions={
          canInvite ? (
            <Button variant="primary" icon={<IconPlus />}>
              {t('invite')}
            </Button>
          ) : null
        }
      />

      <Toolbar />

      <ListPane>
        <DataTable<Member>
          rows={members}
          columns={columns}
          rowKey={(m) => m.userId}
          rowMeta={(m) => ({
            handlingClass: 'neutral',
            struck: m.status === 'disabled',
          })}
          loading={isLoading}
          caption={t('title')}
          empty={<EmptyState title={t('empty')} body={t('emptyBody')} />}
        />
      </ListPane>
    </Workbench>
  );
}

const permissionSummary = (role: Role): string => {
  const perms = ROLE_PERMISSIONS[role];
  if (perms.includes('*')) return 'Everything';
  return perms.slice(0, 14).join('  ') + (perms.length > 14 ? ` … +${perms.length - 14}` : '');
};
